import config from "../config";
import { HttpRequest } from "./httpClient";
import { message } from 'antd';

import { createLogger } from '../utils/logger';
const logger = createLogger('utils');

export {
    findUsers, updateUsers, createUser, removeUser, findAdminUser,
    requestResetPassword, resetPassword, updateUserRole, updateUserStatus, UserData,
    UserDataCreate, findUsersById
};

const request = new HttpRequest(config.endpoints.users);
const requestReset = new HttpRequest(config.endpoints.resetPassword);
const restUrl = config.urls.resetPassword;

interface UserDataCreate {
    id: string;
    email: string;
    name: string;
    role: string;
    phoneNumber: string;
    accountId: string;
    password: string;
    resetToken?: string;
}

interface UserData {
    id: string;
    email: string;
    name: string;
    role: string;
    phoneNumber: string;
    createdAt: number;
    lastUpdate: number;
    status: string;
    accountId: string;
    hashPassword: string;
}

interface NotificationData {
    id: string;
    bean: object;
    type: string;
    provider: string;
    createdAt: number;
    send: boolean;
    resetToken: string;
    expiresAt: number;
}

interface EmailData {
    to: string[];
    from: string;
    data: object;
    subject: string;
    replyTo: string[];
    templateName: string;
    currentUrl: string;
}

interface NotificationRequestData {
    messageId: string;
    receiptHandle: string;
    body: EmailData;
    attributes: object;
    messageAttributes: object;
    md5OfBody: string;
    eventSource: string;
    eventSourceARN: string;
    awsRegion: string;
}

const createUser = async (newUser) => {
    // Generar un password aleatorio para el nuevo usuario
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let randomPassword = '';
    for (let i = 0; i < 20; i++) {
        randomPassword += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    newUser.password = randomPassword;

    try {
        logger.info("Creando usuario...");
        logger.info(newUser);
        const response = await request.post(newUser, 'register');

        if (response.status !== 200 && response.status !== 201) {
            throw {
                response: response,
                message: response.data?.message || 'Error creating user'
            };
        }

        return response;
    } catch (error: any) {
        logger.error('Error creating user', error);
        if (error.response?.data?.message?.includes('User already exists') || error.message?.includes('User already exists')) {
            message.error('User already exists with this email');
        } else {
            message.error('Error creating user. Please try again.');
        }
        throw error;
    }
}

const findAdminUser = async (accountId: string) => {

    try {
        let response: any;
        let users: UserData[];

        response = await request.get(`find/${accountId}`);

        users = response.data.body;

        const admin = users.filter(user =>
            user.role.toLowerCase().includes('admin')
        );
        return admin[0];
    } catch (error) {
        logger.error(error);
    }
}

const findUsers = async (accountId: string, userEmail: string) => {

    try {
        let response: any;
        let users: UserData[];

        if (userEmail && accountId) {
            response = await request.get(`find/${accountId}/${userEmail}`);
        } else if (!userEmail && accountId) {
            // logger.info("Buscando usuarios por accountID...");
            // logger.info(accountId);
            response = await request.get(`find/${accountId}`);
        }

        if (response.status == 200) {
            users = response.data.body;
            return users;
        }
    } catch (error) {
        logger.error(error);
    }
}

const updateUsers = async (newUser: UserDataCreate) => {
    // logger.info("Actualizando usuario...");
    // logger.info(newUser);

    try {
        const response = await request.put('update', newUser);
        // logger.info("Respuesta de la lambda:");
        // logger.info(response);
        return response;
    } catch (error) {
        logger.error('Error updating user', error);
        return null;
    }
}

const removeUser = async (user: string) => {
    logger.info("Eliminando usuario....");
    logger.info(user);

    try {
        const response = await request.delete('remove', { "email": user });
        logger.info('Lambda response', { status: response.status });

        if (response.status === 200) {
            message.success('User removed successfully');
        } else {
            message.error('Error removing user. Please try again.');
        }

        return response;
    } catch (error) {
        logger.error(error);
        message.error('Error removing user. Please try again.');
        throw error;
    }
}

const resetPassword = async (token: string) => {
    try {
        let response: any;
        let notification: NotificationData;

        response = await requestReset.get(`/find/${token}`);

        notification = response.data;

        return notification;
    } catch (error) {
        logger.error("Error en resetPassword:", error);
        return null;
    }
}

const requestResetPassword = async (email: string, template: string, userName: string) => {
    let assignedSubject: string;
    if (template === 'newAccount') {
        assignedSubject = 'Welcome to ECA';
    } else if (template === 'resetPassword') {
        assignedSubject = 'Reset Password';
    }
    try {
        // Use userName if provided, otherwise use email as fallback
        const clientName = userName || email;

        const data = {
            clientName: clientName,
            userEmail: email
        };

        const emailData: Partial<EmailData> = {
            to: [email],
            from: config.sender.email,
            subject: assignedSubject,
            replyTo: [config.sender.email],
            templateName: template,
            currentUrl: restUrl,
            data: data
        };

        // Use the /email endpoint which corresponds to the BatchEmail lambda
        const response = await requestReset.post([emailData], 'email');
        message.success(`An email has been sent to ${email} with instructions to reset your password.`);
    } catch (error) {
        logger.error('Error requesting password reset:', error);
        message.error('An error occurred while requesting the password reset. Please try again.');
    }
}

const updateUserRole = async (userEmail: string, userName: string, newRole: string) => {
    try {
        const response = await request.put('update', {
            email: userEmail,
            name: userName,
            role: newRole
        });

        if (response.status === 200) {
            message.success(`User role updated successfully to ${newRole}`);
            return response;
        } else {
            message.error('Failed to update user role');
            return null;
        }
    } catch (error) {
        logger.error("Error updating user role:", error);
        message.error('Error updating user role');
        return null;
    }
}

const updateUserStatus = async (userEmail: string, userName: string, newStatus: string) => {
    logger.info("Updating user status...");
    logger.info({ userEmail, userName, newStatus });

    try {
        const response = await request.put('update', {
            email: userEmail,
            name: userName,
            status: newStatus
        });

        if (response.status === 200) {
            const statusText = newStatus === 'active' ? 'activated' : 'deactivated';
            message.success(`User ${statusText} successfully`);
            return response;
        } else {
            message.error('Failed to update user status');
            return null;
        }
    } catch (error) {
        logger.error("Error updating user status:", error);
        message.error('Error updating user status');
        return null;
    }
}

const findUsersById = async (userId: string) => {

    try {
        let response: any;
        let users: UserData[];

        if (userId) {
            response = await request.get(`findById/${userId}`);
        } else {
            return null;
        }

        if (response.status == 200) {
            users = response.data;
            return users;
        }
    } catch (error) {
        logger.error(error);
    }
}

